import React, { useEffect, useState } from "react";
import { WaitingNotice } from "@/components/FindoSpinner";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VerificationAnswersForm } from "@/components/Verification";
import { CheckCircle2, PackageSearch, Store, CalendarDays, Tag, ArrowRight, XCircle, ZoomIn, X, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function ObjetTrouve() {
  const { itemId } = useParams();
  const [searchParams] = useSearchParams();
  const adId = searchParams.get("ad");
  const navigate = useNavigate();
  const { t } = useLang();
  const { user, loading: authLoading } = useB2C();

  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound | rejected
  const [rejecting, setRejecting] = useState(false);
  const [showReasons, setShowReasons] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [lightbox, setLightbox] = useState(-1); // -1 closed, else photo index
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [vstatus, setVstatus] = useState(null);

  useEffect(() => { if (data) setVstatus(data.verification_blocked ? "rejected" : (data.verification_status || "none")); }, [data]);

  async function submitVerification(answers) {
    setVerifying(true);
    try {
      await api("/b2c/b2b-match/verify-claim", { method: "POST", b2c: true, body: { item_id: itemId, ad_id: adId, answers } });
      setVerifyOpen(false);
      setVstatus("pending");
      toast.success(t("hc.verificationSentToThePartner"));
    } catch (e) { toast.error(e.message); } finally { setVerifying(false); }
  }

  const REJECT_REASONS = ["Mauvaise couleur", "Pas le bon modèle", "Autre"];
  const REASON_KEYS = { "Mauvaise couleur": "hc.wrongColor", "Pas le bon modèle": "hc.wrongModel", "Autre": "hc.other" };

  const photos = data ? [data.image_base64, data.image_base64_2].filter(Boolean) : [];

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(-1);
  const prevPhoto = (e) => { e?.stopPropagation(); setLightbox((i) => (i - 1 + photos.length) % photos.length); };
  const nextPhoto = (e) => { e?.stopPropagation(); setLightbox((i) => (i + 1) % photos.length); };

  useEffect(() => {
    if (lightbox < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft" && photos.length > 1) prevPhoto();
      else if (e.key === "ArrowRight" && photos.length > 1) nextPhoto();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, photos.length]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/mon-compte?next=/objet-trouve/${itemId}?ad=${adId}`); return; }
    let alive = true;
    api(`/b2c/b2b-match/${itemId}?ad=${encodeURIComponent(adId || "")}`, { b2c: true })
      .then((d) => { if (alive) { setData(d); setState("ready"); } })
      .catch(() => { if (alive) setState("notfound"); });
    return () => { alive = false; };
  }, [authLoading, user, itemId, adId, navigate]);

  function confirmMine() {
    const base = data.declare_path || "/declarer";
    const sep = base.includes("?") ? "&" : "?";
    const qs = new URLSearchParams();
    if (data.category) qs.set("cat", data.category);
    if (data.lost_place) qs.set("place", data.lost_place);
    // Carry the owner's original public-declaration description so the partner
    // form arrives pre-filled (editable).
    if (data.ad_description) qs.set("desc", data.ad_description);
    // Object already confirmed here (cross-match gate) — tell the declaration
    // form so the post-submit CTA skips the redundant "Est-ce bien votre objet ?"
    // step and goes straight to recovery/payment.
    qs.set("verified", "1");
    const target = qs.toString() ? `${base}${sep}${qs.toString()}` : base;
    // Carry the original public-declaration photos via router state (too large
    // for the URL) so the partner form arrives with them pre-filled (editable).
    const photos = [data.ad_image_base64, data.ad_image_base64_2].filter(Boolean);
    navigate(target, photos.length ? { state: { photos } } : undefined);
  }

  async function rejectMine(reason) {
    setRejecting(true);
    try {
      await api("/b2c/b2b-match/reject", { method: "POST", b2c: true, body: { item_id: itemId, ad_id: adId, reason: reason || null } });
      setState("rejected");
    } catch (e) {
      toast.error(e.message || "Une erreur est survenue");
    } finally {
      setRejecting(false);
    }
  }

  const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(document.documentElement.lang || "fr-FR", { day: "numeric", month: "long", year: "numeric" }); } catch { return s; } };

  return (
    <div className="min-h-screen bg-findo-cream">
      <header className="border-b border-findo-navy/10 bg-white/80 backdrop-blur">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/mon-compte" data-testid="objet-trouve-logo"><Logo /></Link>
          <Link to="/mon-compte" className="text-sm font-semibold text-findo-navy hover:text-findo-red transition-colors" data-testid="objet-trouve-account">{t("hc.myAccount")}</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10">
        {state === "loading" && (
          <div className="grid place-items-center py-24 text-findo-navy/60" data-testid="objet-trouve-loading">
            <FindoSpinner size={28} className="mb-3" /> {t("hc.loading")}
          </div>
        )}

        {state === "notfound" && (
          <div className="bg-white rounded-2xl border border-border p-8 text-center card-shadow" data-testid="objet-trouve-notfound">
            <PackageSearch className="mx-auto text-findo-navy/40 mb-3" size={40} />
            <h1 className="font-display font-bold text-xl text-findo-navy mb-2">{t("hc.matchNotFound")}</h1>
            <p className="text-slate-500 text-sm mb-6">{t("hc.thisMatchIsNoLonger")}</p>
            <Button onClick={() => navigate("/mon-compte")} className="rounded-full" data-testid="objet-trouve-back">{t("hc.backToMyAccount")}</Button>
          </div>
        )}

        {state === "rejected" && (
          <div className="bg-white rounded-2xl border border-border p-8 text-center card-shadow" data-testid="objet-trouve-rejected">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={44} />
            <h1 className="font-display font-bold text-xl text-findo-navy mb-2">{t("hc.thankYouForYourFeedback")}</h1>
            <p className="text-slate-500 text-sm mb-6">{t("hc.weHaveInformedThePartner")}</p>
            <Button onClick={() => navigate("/mon-compte")} className="rounded-full" data-testid="objet-trouve-rejected-back">{t("hc.backToMyAccount")}</Button>
          </div>
        )}

        {state === "ready" && data && (
          <div data-testid="objet-trouve-card">
            <p className="text-sm font-semibold text-findo-red mb-1 flex items-center gap-1.5"><PackageSearch size={16} /> {t("hc.itemFoundAtAPartner")}</p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-findo-navy mb-2">{t("hc.isThisYourItem")}</h1>
            <p className="text-slate-500 mb-8 text-sm">{t("hc.checkTheDetailsBelowBefore")}</p>

            <div className="bg-white rounded-2xl border border-border overflow-hidden card-shadow">
              {photos.length > 0 && (
                <div className="relative group">
                  <img
                    src={photos[0]}
                    alt={data.category}
                    onClick={() => openLightbox(0)}
                    className="w-full h-64 object-cover bg-slate-100 cursor-zoom-in"
                    data-testid="objet-trouve-image"
                  />
                  <button
                    type="button"
                    onClick={() => openLightbox(0)}
                    className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur transition-opacity hover:bg-black/75"
                    data-testid="objet-trouve-zoom"
                  >
                    <ZoomIn size={14} /> Agrandir{photos.length > 1 ? ` · ${photos.length} photos` : ""}
                  </button>
                  {photos.length > 1 && (
                    <div className="absolute bottom-3 left-3 flex gap-1.5">
                      {photos.map((p, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => openLightbox(i)}
                          className="h-12 w-12 overflow-hidden rounded-lg border-2 border-white/80 shadow-md transition-transform hover:scale-105"
                          data-testid={`objet-trouve-thumb-${i}`}
                        >
                          <img src={p} alt={`Vue ${i + 1}`} className="h-full w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-3" data-testid="objet-trouve-description">
                  <span className="mt-0.5 h-9 w-9 grid place-items-center rounded-xl bg-findo-navy/5 text-findo-navy shrink-0"><PackageSearch size={18} /></span>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Description</p>
                    <p className="text-findo-navy font-medium">{data.description || "—"}</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Detail icon={<Tag size={16} />} label={t("hc.category")} value={data.category} testid="objet-trouve-category" />
                  <Detail icon={<Store size={16} />} label={t("hc.partner")} value={data.partner_name} testid="objet-trouve-partner" />
                  <Detail icon={<CalendarDays size={16} />} label={t("hc.depositedOn")} value={fmtDate(data.deposit_date)} testid="objet-trouve-date" />
                </div>
              </div>
            </div>

            {vstatus === "approved" ? (
              <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="objet-trouve-verif-approved">
                <p className="font-semibold text-emerald-800 flex items-center gap-1.5"><CheckCircle2 size={18} /> {t("hc.claimValidatedByThePartner")}</p>
                <p className="text-sm text-emerald-700 mt-1 mb-3">{t("hc.youCanNowStartThe")}</p>
                <Button onClick={confirmMine} className="rounded-full h-12 text-base bg-findo-red hover:bg-findo-red/90 gap-2" data-testid="objet-trouve-confirm">
                  {t("hc.continueTheReturn")} <ArrowRight size={18} />
                </Button>
              </div>
            ) : vstatus === "pending" ? (
              <div className="mt-8"><WaitingNotice title={t("hc.verificationPending")} text={t("hc.thePartnerIsReviewingYour")} testid="objet-trouve-verif-pending" /></div>
            ) : vstatus === "rejected" ? (
              <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-5" data-testid="objet-trouve-verif-rejected">
                <p className="font-semibold text-red-700 flex items-center gap-1.5"><XCircle size={18} /> {t("hc.claimNotValidated")}</p>
                <p className="text-sm text-red-600 mt-1">{t("hc.thePartnerConsideredThatThis")}</p>
              </div>
            ) : (
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button onClick={() => setVerifyOpen(true)} className="rounded-full flex-1 h-12 text-base bg-findo-red hover:bg-findo-red/90 gap-2" data-testid="objet-trouve-confirm">
                  {t("hc.thisIsMyItemStart")} <ArrowRight size={18} />
                </Button>
                {!showReasons ? (
                  <Button onClick={() => setShowReasons(true)} variant="outline" className="rounded-full flex-1 h-12 text-base gap-2" data-testid="objet-trouve-reject">
                    <XCircle size={18} /> {t("hc.thisIsNotMyItem")}
                  </Button>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            )}

            {showReasons && (
              <div className="mt-4 rounded-2xl border border-border bg-white p-5 card-shadow" data-testid="objet-trouve-reasons">
                <p className="text-sm font-semibold text-findo-navy mb-1">{t("hc.whyIsThisNotYour")}</p>
                <p className="text-xs text-slate-500 mb-3">{t("hc.yourFeedbackHelpsUsImprove")}</p>
                <div className="flex flex-wrap gap-2">
                  {REJECT_REASONS.map((r) => (
                    <button key={r} onClick={() => (r === "Autre" ? setShowOther(true) : rejectMine(r))} disabled={rejecting}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
                        r === "Autre" && showOther
                          ? "border-findo-navy bg-findo-navy/10 text-findo-navy"
                          : "border-findo-navy/20 text-findo-navy hover:bg-findo-navy/5 hover:border-findo-navy"}`}
                      data-testid={`objet-trouve-reason-${r.replace(/\s+/g, '-').toLowerCase()}`}>
                      {t(REASON_KEYS[r])}
                    </button>
                  ))}
                  <button onClick={() => { setShowReasons(false); setShowOther(false); setOtherText(""); }} disabled={rejecting}
                    className="rounded-full px-4 py-2 text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors"
                    data-testid="objet-trouve-reason-cancel">
                    {t("hc.cancel")}
                  </button>
                </div>
                {showOther && (
                  <div className="mt-4" data-testid="objet-trouve-other-block">
                    <label className="text-sm font-semibold text-findo-navy">{t("hc.specifyYourReason")} <span className="text-findo-red">*</span></label>
                    <textarea value={otherText} onChange={(e) => setOtherText(e.target.value)} rows={3} maxLength={500}
                      placeholder={t("hc.explainWhyThisItemIs")}
                      className="mt-1.5 w-full rounded-xl border border-border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-findo-navy/30 resize-none"
                      data-testid="objet-trouve-other-text" autoFocus />
                    <button onClick={() => rejectMine(otherText.trim())} disabled={rejecting || !otherText.trim()}
                      className="mt-2 rounded-full bg-findo-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-findo-navy/90 disabled:opacity-50 transition-colors"
                      data-testid="objet-trouve-other-submit">
                      {rejecting ? t("hc.sending") : t("hc.send")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <Dialog open={verifyOpen} onOpenChange={(o) => !o && setVerifyOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="objet-trouve-verify-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-findo-navy">{t("hc.identityVerification")}</DialogTitle>
          </DialogHeader>
          <VerificationAnswersForm questions={data?.verification_questions} busy={verifying}
            onSubmit={submitVerification} onCancel={() => setVerifyOpen(false)} />
        </DialogContent>
      </Dialog>

      {lightbox >= 0 && photos.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
          onClick={closeLightbox}
          data-testid="objet-trouve-lightbox"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-5 right-5 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            data-testid="objet-trouve-lightbox-close"
            aria-label={t("hc.close")}
          >
            <X size={22} />
          </button>
          {photos.length > 1 && (
            <button
              type="button"
              onClick={prevPhoto}
              className="absolute left-4 sm:left-8 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
              data-testid="objet-trouve-lightbox-prev"
              aria-label={t("hc.previousPhoto")}
            >
              <ChevronLeft size={26} />
            </button>
          )}
          <img
            src={photos[lightbox]}
            alt={`Vue ${lightbox + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] rounded-xl object-contain shadow-2xl"
            data-testid="objet-trouve-lightbox-image"
          />
          {photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={nextPhoto}
                className="absolute right-4 sm:right-8 grid h-12 w-12 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                data-testid="objet-trouve-lightbox-next"
                aria-label={t("hc.nextPhoto")}
              >
                <ChevronRight size={26} />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white" data-testid="objet-trouve-lightbox-counter">
                {lightbox + 1} / {photos.length}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Detail({ icon, label, value, testid }) {
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <span className="mt-0.5 h-9 w-9 grid place-items-center rounded-xl bg-findo-navy/5 text-findo-navy shrink-0">{icon}</span>
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{label}</p>
        <p className="text-findo-navy font-medium">{value || "—"}</p>
      </div>
    </div>
  );
}
